/**
 * PulsarParaHablar — grabador de voz de "mantené presionado" para el Bolsillo.
 *
 * POR QUÉ: describir un proceso con la voz es más rápido y más fiel que
 * escribirlo en un celular parado en una sala. El audio no es un modo más: es
 * el modo natural de levantar evidencia. Por eso la grabación se opera con UN
 * gesto y sin pasos intermedios:
 *
 *   presionar  -> el micrófono captura DESDE EL TOQUE y un anillo se llena
 *                 alrededor del botón (~1.3 s). Al cerrarse, el botón crece,
 *                 vibra y aparece el cronómetro: quedó ARMADO. Nada de lo
 *                 hablado durante el armado se pierde (ya se venía grabando).
 *   soltar     -> armado: ENVÍA (sin pantalla de confirmación).
 *                 sin armar: se descarta con aviso — un roce o un toque de
 *                 duda nunca envía nada, y el anillo incompleto explica solo
 *                 por qué no pasó nada.
 *   deslizar ↑ -> FIJA la grabación (manos libres): un candado abierto flota
 *                 sobre el botón invitando al gesto; al cruzar el umbral se
 *                 cierra con pop y aparecen botones reales de Aportar y
 *                 Cancelar. Fijado, soltar NO envía: el envío es manual.
 *   deslizar ← -> cancela sin enviar.
 *
 * Pantalla apagada: el navegador NO graba en segundo plano. Mientras se graba
 * pedimos Wake Lock (la pantalla no se apaga sola) y, si la página igual pasa
 * a fondo (llamada, cambio de app), detenemos y RESCATAMOS lo capturado: al
 * volver se ofrece Aportar/Descartar — nunca se auto-envía algo no confirmado
 * ni se pierden minutos de audio por una llamada.
 *
 * Guardas (los aportes son INSERT-only, C1: lo enviado no se borra):
 *   - soltar antes de que el anillo se cierre (ARMADO_MS) -> descarta con aviso.
 *   - soltar mientras el navegador todavía pide permiso -> se descarta con aviso.
 *   - tope duro de MAX_S: al llegar se envía sola en vez de grabar sin fin.
 *
 * Teclado (accesibilidad): Espacio/Enter arranca en modo FIJADO, con botones
 * reales de Aportar y Cancelar — nadie necesita sostener una tecla.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, ChevronUp, Lock, LockOpen, Mic, Send, Trash2, X } from 'lucide-react';
import { Button } from './ui/Button';
import {
  useGrabadorAudio,
  formatearDuracion,
  type GrabacionAudio,
} from '../hooks/useGrabadorAudio';
import { useWakeLock } from '../hooks/useWakeLock';
import { borrarGrabacion } from '../lib/grabacionEnCurso';

/** Píxeles hacia ARRIBA para fijar la grabación (manos libres). */
const UMBRAL_FIJAR = 56;
/** Píxeles hacia la IZQUIERDA para cancelar sin enviar. */
const UMBRAL_CANCELAR = 88;
/**
 * Cuánto sostener para ARMAR la grabación (el anillo tarda esto en cerrarse).
 * El audio corre desde el toque; esto solo decide si soltar envía o descarta.
 */
const ARMADO_MS = 1300;
/** Tope duro de una nota de voz (10 min): al llegar se envía sola. */
const MAX_S = 600;

/** Hasta acá el botón sigue al dedo uno a uno; después empieza a resistir. */
const TRAMO_LIBRE = 20;
/** Cuánto cede el tramo elástico. Calibrado para que el recorrido total al
 *  llegar al umbral de cancelar quede donde estaba antes (~50 px). */
const ELASTICO = 152;

/**
 * Cuánto sube el botón cuando el pulgar se desliza `y` píxeles.
 *
 * Antes era `y * 0.6`: una razón fija, que hace que el botón nunca alcance al
 * dedo ni siquiera al principio, cuando lo único que la persona necesita es
 * confirmar que el gesto se registró. Ahora los primeros 20 px van pegados al
 * pulgar y a partir de ahí la resistencia crece, que es como se comporta
 * cualquier cosa elástica del mundo real: cede primero, se pone dura después.
 * El recorrido total no cambió, así que la geometría del bloque tampoco.
 */
function resistencia(y: number): number {
  if (y <= TRAMO_LIBRE) return Math.round(y);
  const exceso = y - TRAMO_LIBRE;
  return Math.round(TRAMO_LIBRE + (exceso * ELASTICO * 0.55) / (ELASTICO + 0.55 * exceso));
}

/** Vibración corta de confirmación, donde el dispositivo la soporte. */
function vibrar(ms: number) {
  try {
    navigator.vibrate?.(ms);
  } catch {
    /* navegador sin vibración: el gesto igual funciona */
  }
}

export function PulsarParaHablar({
  onListo,
  bloqueado = null,
  actividadId,
}: {
  /**
   * Actividad a la que pertenece la nota. Habilita la persistencia por trozos:
   * lo hablado se escribe a IndexedDB cada 5 s, así que ni el cierre brusco de
   * la pestaña ni la suspensión del teléfono se llevan la grabación.
   */
  actividadId?: string;
  /**
   * Se llama con la grabación terminada; el padre la encola y la sube. Viaja
   * la grabación ENTERA (no solo el blob) porque sus marcas de tiempo son
   * parte del dato: con la cola durable un audio puede entrar al acta mucho
   * después de grabarse, y el acta debe decir cuándo se DIJO.
   */
  onListo: (grabacion: GrabacionAudio) => void;
  /**
   * Motivo por el que TODAVÍA no se puede grabar (falta elegir la categoría,
   * hay una subida en curso…); null = habilitado. Presionar con bloqueo no
   * graba: muestra el motivo en la ranura de avisos — el botón explica, no
   * se apaga en silencio.
   */
  bloqueado?: string | null;
}) {
  const { soportado, estado, duracionS, grabacion, error, iniciar, detener, reiniciar, idEnCurso } =
    useGrabadorAudio(
      actividadId
        ? { persistir: { actividadId, contexto: 'nota', segundos: 5 } }
        : {},
    );

  const [fijado, setFijado] = useState(false);
  const [armado, setArmado] = useState(false);
  const [deslizY, setDeslizY] = useState(0);
  const [porCancelar, setPorCancelar] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  /** Grabación detenida por pasar a fondo: espera Aportar/Descartar manual. */
  const [rescate, setRescate] = useState<GrabacionAudio | null>(null);

  const gestoRef = useRef<{ x: number; y: number; t: number } | null>(null);
  const sueltoAntesRef = useRef(false);
  const rescatadoRef = useRef(false);
  const onListoRef = useRef(onListo);
  onListoRef.current = onListo;

  const grabando = estado === 'grabando';
  const armando = grabando && !armado;

  // La pantalla no se apaga sola mientras se graba.
  useWakeLock(grabando);

  /** Hay grabación lista -> se envía sola (o queda en rescate si vino de fondo). */
  useEffect(() => {
    if (estado !== 'listo' || !grabacion) return;
    if (rescatadoRef.current) {
      rescatadoRef.current = false;
      setRescate(grabacion);
    } else {
      onListoRef.current(grabacion);
    }
    setFijado(false);
    setDeslizY(0);
    setPorCancelar(false);
    setAviso(null);
    reiniciar();
  }, [estado, grabacion, reiniciar]);

  /**
   * Se soltó el botón mientras el navegador todavía pedía permiso: la
   * grabación arranca "huérfana". La descartamos y explicamos el gesto.
   */
  useEffect(() => {
    if (grabando && sueltoAntesRef.current) {
      sueltoAntesRef.current = false;
      reiniciar();
      setAviso('Mantené el botón presionado mientras hablás. Al soltarlo, tu aporte queda registrado.');
    }
  }, [grabando, reiniciar]);

  /**
   * ARMADO: el anillo se cierra a los ARMADO_MS de estar grabando. Fijar
   * (gesto o teclado) arma de inmediato: es un gesto deliberado, no un roce.
   */
  useEffect(() => {
    if (!grabando) {
      setArmado(false);
      return;
    }
    if (fijado) {
      setArmado(true);
      return;
    }
    const t = window.setTimeout(() => {
      setArmado(true);
      vibrar(24);
    }, ARMADO_MS);
    return () => window.clearTimeout(t);
  }, [grabando, fijado]);

  /** Tope duro: en vez de grabar sin fin, se envía. */
  useEffect(() => {
    if (grabando && duracionS >= MAX_S) detener();
  }, [grabando, duracionS, detener]);

  const cancelar = useCallback(() => {
    // Cancelar es una decisión explícita: los trozos se borran para que la
    // recuperación al reabrir solo traiga audio que alguien quiso dejar.
    if (idEnCurso) void borrarGrabacion(idEnCurso);
    gestoRef.current = null;
    sueltoAntesRef.current = false;
    setFijado(false);
    setDeslizY(0);
    setPorCancelar(false);
    reiniciar();
  }, [reiniciar, idEnCurso]);

  /**
   * Red de seguridad de fondo: si la página se oculta grabando (se apagó la
   * pantalla pese al Wake Lock, entró una llamada, cambió de app), el sistema
   * va a cortar el micrófono. Detenemos ya y conservamos lo capturado como
   * rescate; sin armar todavía, no hay nada que valga la pena conservar.
   */
  useEffect(() => {
    if (!grabando) return;
    const alOcultarse = () => {
      if (document.visibilityState !== 'hidden') return;
      if (armado) {
        rescatadoRef.current = true;
        detener();
      } else {
        cancelar();
      }
    };
    document.addEventListener('visibilitychange', alOcultarse);
    return () => document.removeEventListener('visibilitychange', alOcultarse);
  }, [grabando, armado, detener, cancelar]);

  const fijar = useCallback(() => {
    gestoRef.current = null;
    setDeslizY(0);
    setPorCancelar(false);
    setFijado(true);
    vibrar(20);
  }, []);

  const aportarRescate = useCallback(() => {
    if (!rescate) return;
    onListoRef.current(rescate);
    setRescate(null);
  }, [rescate]);

  /* ------------------------------ Gesto ------------------------------ */

  function soltarCaptura(e: React.PointerEvent<HTMLElement>) {
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }

  function onPointerDown(e: React.PointerEvent<HTMLButtonElement>) {
    if (fijado || estado === 'pidiendo') return;
    if (bloqueado) {
      setAviso(bloqueado);
      return;
    }
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    gestoRef.current = { x: e.clientX, y: e.clientY, t: Date.now() };
    sueltoAntesRef.current = false;
    setAviso(null);
    setDeslizY(0);
    setPorCancelar(false);
    vibrar(12);
    void iniciar();
  }

  function onPointerMove(e: React.PointerEvent<HTMLButtonElement>) {
    const ini = gestoRef.current;
    if (!ini || fijado) return;
    const arriba = ini.y - e.clientY;
    const izquierda = ini.x - e.clientX;

    if (izquierda >= UMBRAL_CANCELAR) {
      soltarCaptura(e);
      cancelar();
      setAviso('Grabación cancelada. No se envió nada.');
      vibrar(30);
      return;
    }
    if (arriba >= UMBRAL_FIJAR) {
      soltarCaptura(e);
      fijar();
      return;
    }
    setDeslizY(Math.max(0, arriba));
    setPorCancelar(izquierda >= UMBRAL_CANCELAR / 2);
  }

  function onPointerUp(e: React.PointerEvent<HTMLButtonElement>) {
    soltarCaptura(e);
    if (fijado) return;
    gestoRef.current = null;
    setDeslizY(0);
    setPorCancelar(false);

    // El permiso todavía no llegó: lo resuelve el efecto de arriba.
    if (estado === 'pidiendo') {
      sueltoAntesRef.current = true;
      return;
    }
    // Permiso denegado o dispositivo sin micrófono: el error queda a la vista.
    if (estado === 'error') return;

    // Soltó antes de que el anillo se cerrara: no era una nota de voz.
    if (!grabando || !armado) {
      reiniciar();
      setAviso('Sostené el botón hasta que el anillo se cierre: ahí ya estás grabando. Al soltarlo, tu aporte se envía.');
      return;
    }
    detener();
  }

  function onPointerCancel(e: React.PointerEvent<HTMLButtonElement>) {
    soltarCaptura(e);
    // Si el corte vino de pasar a fondo, el rescate ya se está encargando.
    if (fijado || rescatadoRef.current) return;
    cancelar();
  }

  /** Teclado: Espacio/Enter arranca en modo fijado (manos libres). */
  function onKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (e.key !== ' ' && e.key !== 'Enter') return;
    if (e.repeat || fijado || grabando || estado === 'pidiendo') return;
    e.preventDefault();
    if (bloqueado) {
      setAviso(bloqueado);
      return;
    }
    setAviso(null);
    setFijado(true);
    void iniciar();
  }

  /* ----------------------------- Render ------------------------------ */

  // GEOMETRÍA FIJA (bug real, transcrito por un participante: «creo de que se
  // mueve mucho»): al empezar a grabar el bloque crecía —cronómetro, guía,
  // botón más grande— y todo lo de abajo se corría; el dedo quedaba sobre otra
  // cosa. Regla: el bloque mide SIEMPRE lo mismo (ALTO_BLOQUE) en reposo,
  // grabando, fijado y en rescate; las ranuras internas tienen altura fija y
  // su contenido aparece sin empujar nada; el botón no cambia de tamaño de
  // layout (anillo, presión y armado son overlay/transform) y la ranura de
  // avisos queda reservada aunque esté vacía.
  const ALTO_BLOQUE = 'min-h-[17rem]';

  if (!soportado) {
    return (
      <p className="mt-3 flex items-center gap-2 rounded-md border border-border px-3 py-2 font-sans text-sm text-content-muted">
        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
        Este teléfono no permite grabar audio desde el navegador. Podés adjuntar
        una foto o archivo.
      </p>
    );
  }

  const progresoFijar = Math.min(1, deslizY / UMBRAL_FIJAR);

  return (
    <div className="mt-3">
      {rescate ? (
        /* Rescate: la grabación se cortó por pasar a fondo; lo capturado se
           conserva y el envío es una decisión explícita. Mismo alto: nada se
           mueve al aparecer. */
        <div
          className={`flex ${ALTO_BLOQUE} w-full flex-col items-center justify-center gap-3 rounded-lg border border-warning bg-surface-base px-4 py-4`}
        >
          <div className="flex items-center justify-center gap-2">
            <Mic className="h-5 w-5 text-content-muted" aria-hidden="true" />
            <span className="font-display text-2xl font-semibold tabular-nums text-content">
              {formatearDuracion(rescate.duracionS)}
            </span>
            <span className="font-sans text-xs font-semibold text-content-muted">guardados</span>
          </div>
          <p role="status" className="text-center font-sans text-sm text-content-muted">
            La grabación se interrumpió (¿se apagó la pantalla o cambiaste de
            app?). Lo que dijiste hasta ahí quedó guardado.
          </p>
          <div className="flex w-full gap-2">
            <Button
              variant="secondary"
              onClick={() => setRescate(null)}
              leadingIcon={<Trash2 className="h-5 w-5" aria-hidden="true" />}
            >
              Descartar
            </Button>
            <Button
              variant="primary"
              fullWidth
              onClick={aportarRescate}
              leadingIcon={<Send className="h-5 w-5" aria-hidden="true" />}
            >
              Aportar
            </Button>
          </div>
        </div>
      ) : fijado ? (
        /* Fijado: manos libres, con botones reales de Aportar y Cancelar.
           Mismo alto que el botón de presionar: pasar de un modo a otro no
           mueve la página. */
        <div
          className={`flex ${ALTO_BLOQUE} w-full flex-col items-center justify-center gap-3 rounded-lg border border-error bg-surface-base px-4 py-4`}
        >
          <div className="flex items-center justify-center gap-3">
            <span className="h-3 w-3 animate-pulse rounded-full bg-error" aria-hidden="true" />
            <span className="font-display text-2xl font-semibold tabular-nums text-content">
              {formatearDuracion(duracionS)}
            </span>
            <span className="flex items-center gap-1 font-sans text-xs font-semibold text-content-muted">
              {/* El candado "se cierra" al entrar: pop de confirmación. */}
              <Lock
                className="h-3.5 w-3.5"
                style={{ animation: 'pop-candado 250ms ease-out' }}
                aria-hidden="true"
              />
              Grabando sin sostener
            </span>
          </div>
          <p role="status" className="text-center font-sans text-sm text-content-muted">
            Hablá tranquilo. Cuando termines, aportá.
          </p>
          <div className="flex w-full gap-2">
            <Button
              variant="secondary"
              onClick={cancelar}
              leadingIcon={<Trash2 className="h-5 w-5" aria-hidden="true" />}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              fullWidth
              onClick={detener}
              leadingIcon={<Send className="h-5 w-5" aria-hidden="true" />}
            >
              Aportar
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          /* Este botón administra su propio transform: el círculo rojo se
             escala y sube siguiendo al pulgar. El hundimiento global de la
             capa base encogería el bloque entero durante toda la grabación y
             pelearía con ese gesto. */
          data-sin-hundir=""
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          onKeyDown={onKeyDown}
          onContextMenu={(e) => e.preventDefault()}
          aria-label={
            armando
              ? 'Preparando la grabación. Seguí presionando.'
              : grabando
                ? `Grabando ${formatearDuracion(duracionS)}. Soltá para aportar.`
                : 'Mantené presionado para hablar'
          }
          className={`flex ${ALTO_BLOQUE} w-full touch-none select-none flex-col items-center justify-center gap-2 rounded-lg border px-4 py-4 transition-colors duration-fast focus-visible:outline-none focus-visible:shadow-focus ${
            grabando
              ? porCancelar
                ? 'border-error bg-surface-sunken'
                : 'border-error bg-surface-base'
              : 'border-border-strong bg-surface-base'
          }`}
        >
          {/* Ranura del cronómetro: SIEMPRE mide h-9; aparece recién al armar
              (junto con el pop y la vibración: la señal de "esto ya graba"). */}
          <span className="flex h-9 items-center gap-2">
            {grabando && armado && (
              <>
                <span className="h-3 w-3 animate-pulse rounded-full bg-error" aria-hidden="true" />
                <span className="font-display text-2xl font-semibold tabular-nums text-content">
                  {formatearDuracion(duracionS)}
                </span>
              </>
            )}
          </span>

          {/* Ranura de la guía de fijar: SIEMPRE mide h-7. El candado abierto
              flota (invitación al gesto) y crece a medida que el dedo sube. */}
          <span className="flex h-7 items-center justify-center" aria-hidden={!(grabando && armado)}>
            {grabando && armado && !porCancelar && (
              <span style={{ animation: 'flotar-candado 1.8s ease-in-out infinite' }}>
                <span
                  className="flex items-center gap-1.5 rounded-full border border-border-strong bg-surface-raised px-3 py-1 font-sans text-xs font-semibold text-content"
                  style={{
                    transform: `scale(${1 + progresoFijar * 0.15})`,
                    borderColor: progresoFijar > 0.5 ? 'var(--color-error)' : undefined,
                  }}
                >
                  <ChevronUp
                    className="h-4 w-4"
                    style={{ color: progresoFijar > 0 ? 'var(--color-error)' : undefined }}
                    aria-hidden="true"
                  />
                  <LockOpen className="h-3.5 w-3.5" aria-hidden="true" />
                  Deslizá arriba para grabar sin sostener
                </span>
              </span>
            )}
          </span>

          {/* El botón rojo: tamaño de layout CONSTANTE. El anillo de armado es
              un overlay absoluto, la presión/armado lo escalan con transform y
              el deslizamiento lo eleva — nada empuja nada. */}
          {/* Bloqueado (falta la categoría, subida en curso): el botón se ve
              apagado pero sigue respondiendo — presionar explica el porqué. */}
          {/* Mientras el dedo está abajo NO hay transición: el botón va pegado
              al pulgar, no lo persigue. Una transición sobre este mismo
              transform mete un retraso constante entre el dedo y el círculo, y
              eso es exactamente lo que hace que un gesto se sienta indirecto.
              Al soltar sí vuelve con transición, porque ahí ya no hay dedo que
              seguir y el retorno se agradece suave. */}
          <span
            className={`relative flex h-24 w-24 items-center justify-center rounded-full text-content-inverse shadow-md ${
              grabando ? '' : 'transition-transform duration-fast'
            } ${porCancelar || (bloqueado && !grabando) ? 'bg-content-muted' : 'bg-error'}`}
            style={{
              transform: `translateY(-${resistencia(deslizY)}px) scale(${
                !grabando ? 0.85 : armado ? 1 : 0.9
              })`,
            }}
            aria-hidden="true"
          >
            {/* Anillo de armado: se llena en ARMADO_MS y se desvanece al armar.
                dasharray 314.16 = circunferencia del r=50 (ver index.css). */}
            <svg
              className="absolute -inset-1.5"
              viewBox="0 0 108 108"
              style={{ opacity: armando ? 1 : 0, transition: 'opacity 250ms ease-out' }}
              aria-hidden="true"
            >
              <circle
                cx="54"
                cy="54"
                r="50"
                fill="none"
                stroke="var(--color-error)"
                strokeWidth="5"
                strokeLinecap="round"
                strokeDasharray="314.16"
                transform="rotate(-90 54 54)"
                style={
                  armando
                    ? { animation: `anillo-armado ${ARMADO_MS}ms linear forwards` }
                    : { strokeDashoffset: 314.16 }
                }
              />
            </svg>
            {porCancelar ? <X className="h-10 w-10" /> : <Mic className="h-10 w-10" />}
          </span>

          {/* Ranuras de texto: alturas fijas, solo cambia el contenido. */}
          <span className="flex h-6 items-center font-sans text-base font-semibold text-content">
            {estado === 'pidiendo'
              ? 'Dale permiso al micrófono…'
              : armando
                ? 'Seguí presionando…'
                : grabando
                  ? porCancelar
                    ? 'Soltá para cancelar'
                    : 'Soltá para aportar'
                  : 'Mantené presionado para hablar'}
          </span>
          <span className="flex h-5 items-center font-sans text-xs text-content-muted">
            {armando
              ? 'Cuando el anillo se cierre, quedás grabando'
              : grabando
                ? 'Deslizá a la izquierda para cancelar'
                : 'Se transcribe sola y queda en el acta con tu nombre'}
          </span>
        </button>
      )}

      {/* Ranura de avisos: reservada SIEMPRE — un error o un aviso no empuja
          la página cuando aparece. */}
      <div className="mt-2 min-h-10">
        {estado === 'error' && error ? (
          <p role="alert" className="flex items-start gap-1.5 font-sans text-sm text-error">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            {error}
          </p>
        ) : aviso ? (
          <p role="status" className="flex items-start gap-1.5 font-sans text-sm text-content-muted">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            {aviso}
          </p>
        ) : null}
      </div>
    </div>
  );
}
